<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Data exports') }}
    </template>
  </PageHeader>
  <div class="data-exports">
    <div class="data-exports-row">
      <div>
        <label class="c-gray">{{ $t('Report type') }}</label>
        <br />
        <select v-model="type.data" class="form-control bgm-white">
          <option v-for="reportType in reportTypes" :key="reportType.id" :value="reportType.id">
            {{ reportType.label }}
          </option>
        </select>
      </div>

      <div v-if="divisions.length > 0">
        <label class="c-gray">{{ $t('Divisions') }}</label>
        <br />
        <BaseMultiSelect
          v-model="division.data"
          :options="divisions"
          :locked-width="true"
          class="p-0"
        >
          <template #all-selected>
            {{ $t('All divisions') }}
          </template>
          <template #no-selection>
            {{ $t('Select divisions') }}
          </template>
        </BaseMultiSelect>
      </div>

      <div>
        <label class="c-gray">{{ $t('Dates') }}</label>
        <br />
        <div class="p-t-5">
          <InputDateRange v-model:date-range="dateRange.data" :format="dateFormat" />
        </div>
      </div>

      <div>
        <ButtonWithSpinner
          :disabled="!canExport"
          :status="buttonStatus"
          :text-while-working="$t('Generating')"
          color="info"
          class="btn-md btn-info"
          @click="generateExport"
        >
          {{ $t('Export data') }}
          <i class="m-l-10 fa fa-download" />
        </ButtonWithSpinner>
      </div>
    </div>

    <div class="row">
      <div v-if="errorMessage" class="alert alert-danger m-t-30" role="alert">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success m-t-30" role="alert">
        {{ successMessage }}
      </div>
      <div
        v-if="successMessage && type.data === 'timesheet'"
        class="alert alert-info m-t-4"
        role="alert"
      >
        {{ $t('Timesheet export will contain data from April 2024 onwards.') }}
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'

import ButtonWithSpinner from '@/components/ButtonWithSpinner.vue'
import BaseMultiSelect from '@/components/BaseMultiSelect.vue'
import InputDateRange from '@/components/InputDateRange.vue'
import QueryEncoder from '@/utils/Encoders/QueryEncoder'
import DateRange from '@/vue-prop-types/DateRange'
import MultiSelectDecodeBuilderMixin from '@/mixins/MultiSelectDecodeBuilderMixin'
import PageHeader from '@/components/PageHeader.vue'

export default {
  components: {
    PageHeader,
    ButtonWithSpinner,
    BaseMultiSelect,
    InputDateRange,
  },

  mixins: [MultiSelectDecodeBuilderMixin],

  props: {
    divisions: {
      type: Array,
      default: () => {},
    },
    timesheetFeatureFlag: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const dateFormat = 'YYYY-MM-DD'

    return {
      buttonStatus: 'enabled',
      errorMessage: null,
      successMessage: null,

      dateFormat,

      reportTypes: [
        { id: null, label: this.$t('Select the report') },
        { id: 'job', label: this.$t('Job') },
        { id: 'hire', label: this.$t('Hire') },
        { id: 'bill', label: this.$t('Bill') },
        this.timesheetFeatureFlag ? { id: 'timesheet', label: this.$t('Timesheet') } : undefined,
      ].filter((item) => item !== undefined),

      type: new QueryEncoder({
        storageKey: 'data_export_type',
        queryPoint: 'type',
        defaultVal: null,
      }),

      division: new QueryEncoder({
        storageKey: 'data_export_division',
        queryPoint: 'division',
        decoder: this.multiSelectDecodeBuilder(this.divisions),
        defaultVal: [],
      }),

      dateRange: new QueryEncoder({
        storageKey: `data_export_dates_${dateFormat}`,
        queryPoint: 'dates',
        decoder: (dateStr) => DateRange.fromString({ str: dateStr, format: dateFormat }),
        encoder: (date) => `${date.start},${date.end}`,
        defaultVal: new DateRange({ format: dateFormat }),
      }),
    }
  },

  computed: {
    canExport() {
      return (
        this.buttonStatus === 'enabled' &&
        this.type.data !== null &&
        (this.divisions.length === 0 ||
          (this.divisions.length > 0 && this.division.data.length > 0)) &&
        this.dateRange.data.isFilled
      )
    },
  },

  methods: {
    generateExport() {
      this.buttonStatus = 'working'
      this.errorMessage = null
      this.successMessage = null

      let companies = []

      if (this.divisions.length > 0) {
        companies =
          this.division.data.length > 0 ? this.division.data.map((division) => division.id) : [null]
      } else {
        companies = [this.account.id]
      }

      this.$apollo
        .mutate({
          mutation: gql`
            mutation (
              $accountId: Int!
              $accountType: String!
              $userId: Int!
              $impersonatorId: Int
              $type: String!
              $generatorType: String!
              $deliveries: [String!]!
              # Filters
              $companies: [Int!]!
              $dateFrom: Date!
              $dateTo: Date!
              $meta: String
            ) {
              createExport(
                input: {
                  accountId: $accountId
                  accountType: $accountType
                  userId: $userId
                  impersonatorId: $impersonatorId
                  type: $type
                  generatorType: $generatorType
                  deliveries: $deliveries
                  args: { companies: $companies, dateFrom: $dateFrom, dateTo: $dateTo, meta: $meta }
                }
              ) {
                status
                message
              }
            }
          `,
          variables: {
            accountId: this.account.id,
            accountType: this.account.type,
            userId: this.user.id,
            impersonatorId: this.impersonator?.id,
            type: this.type.data,
            generatorType: 'csv',
            deliveries: ['email', 'in_app'],

            // Filters
            companies,
            dateFrom: this.dateRange.data.DateStart.format('YYYY-MM-DD'),
            dateTo: this.dateRange.data.DateEnd.format('YYYY-MM-DD'),
            meta: JSON.stringify({
              includeCancelledContracts:
                this.account.settings?.include_cancelled_contracts_in_exports ?? false,
            }),
          },
        })
        .then((data) => {
          this.successMessage = data.data.createExport.message

          setTimeout(() => {
            this.successMessage = null
          }, 5000)
        })
        .catch(() => {
          this.errorMessage = this.$t('An error occurred. Please try again')
        })
        .finally(() => {
          this.buttonStatus = 'enabled'
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.data-exports {
  min-height: 50vh;
}
.data-exports-row {
  display: flex;
  justify-items: flex-start;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 20px 35px;
}
</style>
